/**
 * Store, types, and validation for RunMetricChange configuration.
 * This file is separate from RunMetricChangeSection.tsx to avoid import cycles.
 * DO NOT import React components or files that import React components here.
 */
import {assertUnreachable} from '@wandb/weave/common/util/types';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {reportTriggerConfigParseError} from '../configParseError';
import {EventTriggeringConditionType} from '../TriggerEventTypes';
import {type Trigger} from '../TriggersTable/utilTypes';
import {
  getRunFilterConfigFromTriggerFilter,
  getRunMetricRunFilterString,
  isValidRunFilterConfig,
  type RunFilter,
  type RunFilterConfig,
} from './RunFilterShared';
import {
  getAggOpStr,
  getNumberValueForField,
  isValidMetricName,
  isValidWindowSize,
  type MetricFilterAggOp,
} from './RunMetricThresholdStore';

export type ChangeDirection = 'INCREASE' | 'DECREASE' | 'ANY';
export const isValidChangeDirection = (changeDirection: ChangeDirection) => {
  return (
    changeDirection === 'INCREASE' ||
    changeDirection === 'DECREASE' ||
    changeDirection === 'ANY'
  );
};

export type ChangeDirectionOption = {
  value: ChangeDirection;
  label: string;
};
export const changeDirectionOptions: ChangeDirectionOption[] = [
  {value: 'INCREASE', label: 'Increases by at least'},
  {value: 'DECREASE', label: 'Decreases by at least'},
  {value: 'ANY', label: 'Increases or decreases by at least'},
];

export const getChangeDirectionOption = (changeDirection: ChangeDirection) => {
  return changeDirectionOptions.find(
    option => option.value === changeDirection
  );
};

export type RunMetricChangeType = 'ABSOLUTE' | 'RELATIVE';
export const isValidChangeType = (changeType: RunMetricChangeType) => {
  return changeType === 'ABSOLUTE' || changeType === 'RELATIVE';
};
// The ToggleButtonGroup is weird because it's `value` is also the string label,
// so we need to store the enum value separately
export type ChangeTypeOption = {
  value: string; // shown in the UI
  enumValue: RunMetricChangeType; // what we use in the store
};
export const changeTypeOptions: ChangeTypeOption[] = [
  {value: 'Relative', enumValue: 'RELATIVE'},
  {value: 'Absolute', enumValue: 'ABSOLUTE'},
];
export const getChangeTypeOptionForToggleValue = (toggleValue: string) => {
  return changeTypeOptions.find(option => option.value === toggleValue);
};
export const getChangeTypeOptionForEnumValue = (
  enumValue: RunMetricChangeType
) => {
  return changeTypeOptions.find(option => option.enumValue === enumValue);
};
const defaultChangeType: RunMetricChangeType = 'RELATIVE';

export type MetricChangeFilter = {
  metricName: string;
  priorSize: string;
  currentSize: string;
  aggOp: MetricFilterAggOp;
  changeAmount: string;
  changeType: RunMetricChangeType;
  changeDirection: ChangeDirection;
  panelLink: string | undefined;
};

export type RunMetricChangeConfig = {
  metricFilter: Partial<MetricChangeFilter> &
    Pick<
      MetricChangeFilter,
      'priorSize' | 'currentSize' | 'aggOp' | 'changeType' | 'changeDirection'
    >;
  runFilter: RunFilterConfig;
  allowAutoFocus: boolean;
};

const DEFAULT_PRIOR_WINDOW_SIZE = 50;
const DEFAULT_CURRENT_WINDOW_SIZE = 10;

const initialRunMetricConfig: RunMetricChangeConfig = {
  metricFilter: {
    metricName: undefined,
    priorSize: DEFAULT_PRIOR_WINDOW_SIZE.toString(),
    currentSize: DEFAULT_CURRENT_WINDOW_SIZE.toString(),
    aggOp: 'AVERAGE',
    changeAmount: undefined,
    changeType: defaultChangeType,
    changeDirection: 'ANY',
  },
  runFilter: {
    isRunNameFilterEnabled: false,
    runName: undefined,
    isUsernameFilterEnabled: false,
    username: undefined,
  },
  allowAutoFocus: true,
};

export const useRunMetricChangeStore = create<RunMetricChangeConfig>()(
  devtools(
    () => ({
      ...initialRunMetricConfig,
    }),
    {name: 'RunMetricChangeStore'}
  )
);

export const RunMetricChangeActions = {
  initAsync: async (suggestedMetricName?: string, panelLink?: string) => {
    useRunMetricChangeStore.setState({
      ...initialRunMetricConfig,
      metricFilter: {
        ...initialRunMetricConfig.metricFilter,
        metricName: suggestedMetricName,
        panelLink: panelLink,
      },
      allowAutoFocus: suggestedMetricName == null,
    });
  },
  initFromTrigger: (trigger: Trigger) => {
    const config = getRunMetricChangeConfig(trigger);
    if (config) {
      useRunMetricChangeStore.setState(config);
    }
  },

  setMetricName: (metricName: string) => {
    setMetricFilter({metricName});
  },
  setAggOp: (aggOp: MetricFilterAggOp) => {
    setMetricFilter({aggOp});
  },
  setChangeAmount: (changeAmount: string) => {
    // note that for the % type, we store the raw value for now (eg 20 for 20%),
    // will translate it to % when saving to API
    setMetricFilter({changeAmount});
  },
  setRunName: (runName: string) => {
    setRunFilter({runName});
  },
  setPriorSize: (priorSize: string) => {
    setMetricFilter({priorSize});
  },
  setCurrentSize: (currentSize: string) => {
    setMetricFilter({currentSize});
  },
  setIsRunNameFilterEnabled: (isRunNameFilterEnabled: boolean) => {
    setRunFilter({isRunNameFilterEnabled});
  },
  setChangeDirection: (changeDirection: ChangeDirection) => {
    setMetricFilter({changeDirection});
  },
  setChangeType: (changeType: RunMetricChangeType) => {
    setMetricFilter({changeType});
  },
  setUsername: (username: string | undefined) => {
    setRunFilter({username});
  },
  setIsUsernameFilterEnabled: (isUsernameFilterEnabled: boolean) => {
    setRunFilter({isUsernameFilterEnabled});
  },
};

const setMetricFilter = (metricFilter: Partial<MetricChangeFilter>) => {
  useRunMetricChangeStore.setState(state => ({
    metricFilter: {...state.metricFilter, ...metricFilter},
  }));
};

const setRunFilter = (runFilter: Partial<RunFilter>) => {
  useRunMetricChangeStore.setState(state => ({
    runFilter: {...state.runFilter, ...runFilter},
  }));
};

export const isValidChangeAmount = (changeAmount: string | undefined) => {
  if (changeAmount === undefined || changeAmount === '') {
    return false;
  }
  const changeAmountNumber = getNumberValueForField(changeAmount);
  return !isNaN(changeAmountNumber) && isFinite(changeAmountNumber);
};

export const isValidRunMetricChangeConfig = (config: RunMetricChangeConfig) => {
  return (
    isValidMetricName(config.metricFilter.metricName) &&
    isValidChangeAmount(config.metricFilter.changeAmount) &&
    isValidWindowSize(config.metricFilter.priorSize) &&
    isValidWindowSize(config.metricFilter.currentSize) &&
    // aggOp is currently hardcoded. For the futurenote that undefined is a valid
    //   value for aggOp - it means "take the last value and ignore windowSize"
    //   which is not something the UI currently supports, but it's not an error
    isValidChangeDirection(config.metricFilter.changeDirection) &&
    isValidChangeType(config.metricFilter.changeType) &&
    isValidRunFilterConfig(config.runFilter)
  );
};

export const getEventConfigForRunMetricChange = (
  config: RunMetricChangeConfig
) => {
  return {
    eventTriggeringConditionType: EventTriggeringConditionType.RunMetric,
    filters: {
      filters: [
        {
          metricFilter: {...config.metricFilter, aggOp: 'AVERAGE'},
          runFilter: config.runFilter,
        },
      ],
    },
  };
};

// this is used when we're looking at an already created trigger in the UI
export const getRunMetricChangeConfig = (
  trigger: Trigger
): RunMetricChangeConfig | undefined => {
  if (
    !trigger.triggeringCondition.filter ||
    trigger.triggeringCondition.eventType !==
      EventTriggeringConditionType.RunMetricChange
  ) {
    return undefined;
  }

  try {
    const parsedFilter = JSON.parse(trigger.triggeringCondition.filter);
    const runFilter = getRunFilterConfigFromTriggerFilter(parsedFilter);
    if (!runFilter) {
      reportTriggerConfigParseError('run_metric_change', trigger);
      return undefined;
    }
    const metricFilter = parsedFilter.run_metric_filter.change_filter;
    if (!metricFilter) {
      reportTriggerConfigParseError('run_metric_change', trigger);
      return undefined;
    }

    let numChangeAmount = metricFilter.change_amount;
    // this makes displaying/editing the percentage value easier
    if (metricFilter.change_type === 'RELATIVE') {
      numChangeAmount = numChangeAmount * 100;
    }

    return {
      metricFilter: {
        metricName: metricFilter.name,
        priorSize: metricFilter.prior_window_size.toString(),
        currentSize: metricFilter.current_window_size.toString(),
        aggOp: metricFilter.agg_op,
        changeAmount: numChangeAmount.toString(),
        changeType: metricFilter.change_type,
        changeDirection: metricFilter.change_dir,
        panelLink: metricFilter.panel_link,
      },
      runFilter,
      allowAutoFocus: false,
    };
  } catch (e) {
    reportTriggerConfigParseError('run_metric_change', trigger, e);
    return undefined;
  }
};

export const getDirectionAndAmountString = (
  metricFilter: RunMetricChangeConfig['metricFilter']
) => {
  let changeAmountStr = metricFilter.changeAmount
    ? `${metricFilter.changeAmount}`
    : '{Metric threshold}';
  if (metricFilter.changeType === 'RELATIVE') {
    changeAmountStr = `${changeAmountStr}%`;
  }
  let changeDirectionStr;
  switch (metricFilter.changeDirection) {
    case 'INCREASE':
      changeDirectionStr = 'increases';
      break;
    case 'DECREASE':
      changeDirectionStr = 'decreases';
      break;
    case 'ANY':
      changeDirectionStr = 'increases or decreases';
      break;
    default:
      assertUnreachable(metricFilter.changeDirection);
  }
  return {
    changeDirectionStr,
    changeAmountStr,
  };
};
export const getRunMetricChangeString = (
  metricFilter: RunMetricChangeConfig['metricFilter'],
  runFilter: RunMetricChangeConfig['runFilter']
) => {
  let ret = getRunMetricRunFilterString(runFilter);
  const aggOpStr = getAggOpStr(metricFilter.aggOp);

  const metricNameStr = metricFilter.metricName
    ? `${metricFilter.metricName}`
    : '{Metric}';

  const currentSizeStr = metricFilter.currentSize
    ? `${metricFilter.currentSize}`
    : '{Current window}';
  const priorSizeStr = metricFilter.priorSize
    ? `${metricFilter.priorSize}`
    : '{Prior window}';

  const {changeDirectionStr, changeAmountStr} =
    getDirectionAndAmountString(metricFilter);

  ret +=
    `${aggOpStr} of ${metricNameStr} for the last ${currentSizeStr} ` +
    `logged values compared to the ${aggOpStr} of ${metricNameStr} during the prior ${priorSizeStr} logged values ` +
    `${changeDirectionStr} by at least` +
    ` ${changeAmountStr}.`;
  return ret;
};
export {isValidMetricName, isValidWindowSize};
